import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Stack,
  useToast,
  FormControl,
  FormErrorMessage,
  Tag,
  TagLeftIcon,
  TagCloseButton,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from 'react-query';

import Card from '../../components/Card';
import CallToAction from '../../components/Card/CallToAction';
import { addDiscountCode, removeDiscountCode } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import { useLoadingToast } from '../../utils/useLoadingToast';

import DiscountTagIcon from '../../components/Icons/DiscountTag';

const DiscountCodeContainer = styled.div.attrs({
  className: 'awt-discount-code-container',
})`
  padding-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DiscountCodeTitle = styled.div.attrs({
  className: 'awt-discount-code-container',
})`
  padding: 8px 0px 8px 0px;
`;

export const BOX_DISCOUNT_PREFIX = 'box-discount-';

const DiscountCodeCard = ({
  appliedDiscountCodes = [],
}: {
  appliedDiscountCodes: Discount[];
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [discountCode, setDiscountCode] = useState('');
  const { customerId, subscriptionId, authToken } = useGlobalContext();
  const [isInputShown, setIsInputShown] = useState(false);
  const { addLoadingToast, removeLoadingToast } = useLoadingToast();

  const [discountCodes, setDiscountCodes] =
    useState<Discount[]>(appliedDiscountCodes);

  const [isCodeInvalid, setIsCodeInvalid] = useState(false);

  const refreshSubscription = async () => {
    await queryClient.invalidateQueries([
      'subscription',
      customerId,
      subscriptionId,
    ]);

    await queryClient.refetchQueries([
      'subscription',
      customerId,
      subscriptionId,
    ]);
  };

  const { mutate: addDiscountCodeMutation, isLoading } = useMutation(
    (discountCode: string) => {
      setIsCodeInvalid(false);
      return addDiscountCode(
        discountCode,
        customerId,
        subscriptionId,
        authToken,
      );
    },

    {
      onSuccess: async (data, discountCode) => {
        queryClient.setQueryData<Subscription>(
          ['subscription', customerId, subscriptionId],
          (old) => {
            const newDiscountCodes = [
              ...(old?.DiscountCodes as Discount[]),
              { title: discountCode, id: discountCode },
            ];
            return {
              ...(old as Subscription),
              DiscountCodes: newDiscountCodes,
            };
          },
        );
        toast({
          description: t('discountCodeCard.successMsg'),
          status: 'success',
          isClosable: true,
          position: 'top',
        });
        setDiscountCode('');
        setIsInputShown(false);
        setDiscountCodes([
          ...discountCodes,
          { title: discountCode, id: discountCode },
        ]);

        refreshSubscription();
      },
      onError: (err) => {
        setIsCodeInvalid(true);
        console.error(err);
      },
    },
  );

  const { mutate: removeDiscountCodeMutation, isLoading: isLoadingRemoving } =
    useMutation(
      (discount: { discountId: string; discountCode: string }) => {
        setIsCodeInvalid(false);
        addLoadingToast();
        return removeDiscountCode(
          discount.discountId,
          discount.discountCode,
          customerId,
          subscriptionId,
          authToken,
        );
      },

      {
        onSuccess: async (_data, { discountId }) => {
          removeLoadingToast();
          toast({
            description: t('discountCodeCard.removeSuccessMsg'),
            status: 'success',
            isClosable: true,
            position: 'top',
          });

          setDiscountCodes(
            discountCodes.filter((discount) => discount.id !== discountId),
          );

          queryClient.setQueryData<Subscription>(
            ['subscription', customerId, subscriptionId],
            (old) => {
              const newDiscountCodes = old?.DiscountCodes?.filter(
                (discount) => discount.id !== discountId,
              );

              return {
                ...(old as Subscription),
                DiscountCodes: newDiscountCodes,
              };
            },
          );

          refreshSubscription();
        },
        onError: (err) => {
          removeLoadingToast();
          console.error(err);
        },
      },
    );

  const handleOnSubmit = (e) => {
    e.preventDefault();

    addDiscountCodeMutation(discountCode);
  };

  const handleOnChange = (e) => {
    setDiscountCode(e.target.value);
  };

  const handleCodeRemove = (discountToRemove) => {
    removeDiscountCodeMutation({
      discountId: discountToRemove.id,
      discountCode: discountToRemove.title,
    });
  };

  return (
    <Card
      ariaLabel={
        isInputShown
          ? t('aria_labels.add_discount_code')
          : t('aria_labels.cancel_add_discount_code')
      }
      headerText={t('discountCodeCard.title')}
      btnText={
        isInputShown
          ? t('discountCodeCard.cancelBtn')
          : t('discountCodeCard.addBtn')
      }
      onClick={() => {
        isInputShown ? setIsInputShown(false) : setIsInputShown(true);
        setIsCodeInvalid(false);
      }}
      className="bundle-card--discount-code"
    >
      {discountCodes.length > 0 && (
        <DiscountCodeContainer>
          {discountCodes.map((discount) => {
            return (
              <Tag key={discount.id} size="md" borderRadius="full">
                <TagLeftIcon as={DiscountTagIcon} />
                <DiscountCodeTitle>
                  {discount.title.startsWith(BOX_DISCOUNT_PREFIX)
                    ? t('discountCodeCard.boxDiscount')
                    : discount.title}
                </DiscountCodeTitle>
                {discount.id !== discount.title &&
                  discount.title.indexOf(BOX_DISCOUNT_PREFIX) === -1 && (
                    <TagCloseButton
                      onClick={() => handleCodeRemove(discount)}
                      isDisabled={isLoadingRemoving}
                    />
                  )}
              </Tag>
            );
          })}
        </DiscountCodeContainer>
      )}
      {isInputShown && (
        <form onSubmit={handleOnSubmit}>
          <Stack spacing={5} paddingTop="8px">
            <FormControl isInvalid={isCodeInvalid}>
              <Input
                aria-label={t('discountCodeCard.title')}
                isInvalid={isCodeInvalid}
                placeholder={t('discountCodeCard.inputPlaceholder')}
                onChange={handleOnChange}
                errorBorderColor="red.300"
                isDisabled={isLoading}
                value={discountCode}
              />
              {isCodeInvalid && (
                <FormErrorMessage>
                  {t('discountCodeCard.errorMsg')}
                </FormErrorMessage>
              )}
            </FormControl>
            <CallToAction
              type="submit"
              onClick={handleOnSubmit}
              isLoading={isLoading}
              width="full"
            >
              {t('discountCodeCard.applyBtn')}
            </CallToAction>
          </Stack>
        </form>
      )}
    </Card>
  );
};

export default DiscountCodeCard;
